import { MutableRefObject, useRef } from "react";
import { Id, Slide, ToastOptions, TypeOptions, toast } from "react-toastify";
import { Spin } from "antd";

interface ToastNotificationProps {
  type: TypeOptions | "loading" | "dismiss";
  title?: string;
}

const toastOptions: ToastOptions = {
  containerId: "A",
  position: "top-right",
  transition: Slide,
  autoClose: 3000,
  hideProgressBar: false,
  theme: "light",
  pauseOnHover: false,
  closeOnClick: true,
};

export function useToast() {
  const id = useRef<Id>();

  const toggleToastID = (
    props: ToastNotificationProps & {
      id?: MutableRefObject<any>;
    },
  ) => {
    const { type, title } = props;

    if (id.current) {
      switch (type) {
        case "success": {
          toast.update(id.current, {
            ...toastOptions,
            render: title || "success",
            type: "success",
          });
          break;
        }
        case "error": {
          toast.update(id.current, {
            ...toastOptions,
            render: title || "error",
            type: "error",
          });
          break;
        }
        case "warning": {
          toast.update(id.current, {
            ...toastOptions,
            render: title || "warning",
            type: "warning",
          });
          break;
        }
        case "info": {
          toast.update(id.current, {
            ...toastOptions,
            render: title || "info",
            type: "info",
          });
          break;
        }
        case "dismiss": {
          toast.dismiss(id.current);
          break;
        }
        default: {
          break;
        }
      }
    } else if (type === "loading") {
      id.current = toast(
        <div className="flex items-center">
          <Spin spinning size="default" />
          <span className="ml-2 text-md">{title || "Loading"}</span>
        </div>,
        { ...toastOptions, theme: "colored", autoClose: false, onClose: () => (id.current = undefined) },
      );
    } else {
      toggleToast(props);
    }
  };

  const toggleToast = (props: ToastNotificationProps) => {
    const { type, title } = props;

    switch (type) {
      case "success": {
        toast.success(title || "success", { ...toastOptions });
        break;
      }
      case "error": {
        toast.error(title || "failure", { ...toastOptions });
        break;
      }
      case "warning": {
        toast.warning(title || "warning", { ...toastOptions });
        break;
      }
      case "info": {
        toast.info(title || "", { ...toastOptions });
        break;
      }
      default: {
        return;
      }
    }
  };

  return { toggleToast, toggleToastID };
}
